import { useQuery } from "@tanstack/react-query";
import { type JSX, useCallback, useState } from "react";
import { GatewayCreateDialog } from "@/app/mcp-gateways/components/create-dialog/gateway-create-dialog";
import type { McpServer } from "@/app/mcp-gateways/types";
import { useGetOnboardingProgress } from "@/app/onboarding/api/hooks";
import { useReconcileOnboardingProgress } from "@/app/onboarding/hooks/use-reconcile-onboarding-progress";
import {
  type Jtbd,
  jtbdsQueryOptions,
} from "@/app/onboarding-first-win/api/get-jtbds";
import { OnboardingPageTitle } from "@/app/onboarding-first-win/components/onboarding-page-title";
import { useOnboardingTelemetry } from "@/app/onboarding-first-win/hooks/use-onboarding-telemetry";
import { JtbdCard } from "./jtbd-card";
import { JtbdDetailDialog } from "./jtbd-detail-dialog";

export function JtbdPicker(): JSX.Element {
  const reconcileOnboardingProgress = useReconcileOnboardingProgress();
  const { data: jtbdsData, isPending: isJtbdsPending } = useQuery(
    jtbdsQueryOptions()
  );
  const { data: progress } = useGetOnboardingProgress();
  const [selected, setSelected] = useState<Jtbd | null>(null);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const { captureOnboardingEvent } = useOnboardingTelemetry();

  const handleCustomGatewayCreated = useCallback(
    async (gateway: McpServer) => {
      await reconcileOnboardingProgress(gateway);
      captureOnboardingEvent("Gateway create dialog submitted", {
        current_step: "jtbd",
        gateway_template: "custom",
      });
    },
    [captureOnboardingEvent, reconcileOnboardingProgress]
  );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-auto px-4 pt-8 pb-8 md:px-8 md:pt-12 md:pb-10">
        <div className="mx-auto w-full max-w-5xl">
          <div className="mb-6">
            <OnboardingPageTitle>
              What do you want to get done?
            </OnboardingPageTitle>
            <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
              Pick an outcome. We&apos;ll set up an MCP gateway so you can
              securely access the right tools from a single URL.
            </p>
          </div>
          <div className="grid grid-cols-1 content-start gap-4 sm:grid-cols-3">
            {(jtbdsData?.jtbds ?? []).map((jtbd) => (
              <JtbdCard
                disabled={isJtbdsPending}
                isCurrentGoal={
                  progress?.gateway != null &&
                  progress.gateway.name === jtbd.name
                }
                jtbd={jtbd}
                key={jtbd.id}
                onClick={() => {
                  setSelected(jtbd);
                  captureOnboardingEvent("Job to be done selected", {
                    current_step: "jtbd",
                    jtbd_id: jtbd.id,
                  });
                }}
              />
            ))}
          </div>
          <div className="mt-6">
            <button
              className="text-muted-foreground/60 text-xs italic transition-colors hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isJtbdsPending}
              onClick={() => setShowCustomDialog(true)}
              type="button"
            >
              or set up your own
            </button>
          </div>
        </div>
      </div>

      <JtbdDetailDialog
        jtbd={selected}
        onClose={() => setSelected(null)}
        open={selected !== null}
      />

      <GatewayCreateDialog
        initialSelectedToolkits={[]}
        initialSelectedTools={[]}
        navigateOnCreate={false}
        onOpenChange={setShowCustomDialog}
        onSuccess={handleCustomGatewayCreated}
        open={showCustomDialog}
      />
    </>
  );
}
